// Privacy Policy, Terms of Use and HVAC/Website Disclaimer — drafted for
// the open-items review, item 11.
//
// THESE ARE DRAFTS FOR COMPARISON, NOT REVIEWED LEGAL ADVICE. Independent
// versions are being commissioned to compare against; `LEGAL_STATUS` in
// nav.ts prints a review banner on all three pages until that comparison is
// done. I am not a lawyer and this text has not been through counsel.
//
// What I can vouch for is the FACTUAL half — the privacy policy describes
// what this codebase actually does, checked against the schema in
// database/migrations and the handlers in backend/src, rather than against
// a template:
//
//   · contact/lead fields          → the shared contact schema
//   · decision-tree answers        → diagnosis_sessions.answers
//   · AI results                   → diagnosis_sessions.ai_result
//   · device cookie + IP           → devices, ai_usage.ip (365-day cookie)
//   · reminder emails              → subscribers, reminder_subscriptions
//   · leads leaving the system     → leads_outbox, relayed to the LeadGen
//                                    ingest and on to an HVAC professional
//   · no analytics or ad trackers  → verified: no gtag/GTM/pixel anywhere
//
// If any of that changes, this file changes in the same commit. A privacy
// policy that has drifted from the code is worse than none.

export interface LegalClause {
    h?: string
    p?: string
    list?: string[]
}

export interface LegalDoc {
    id: 'privacy' | 'terms' | 'disclaimer'
    title: string
    kicker: string
    /** One-line summary shown under the title. */
    lede: string
    clauses: LegalClause[]
}

const CONTACT_LINE =
    'Questions about this page can be sent to info@ask-hvac.com. That mailbox is monitored for website issues, corrections and privacy requests — it is not an HVAC service or sales line, and it cannot dispatch anyone to your home.'

/* ------------------------------------------------------------------ */
/* Privacy Policy                                                      */
/* ------------------------------------------------------------------ */

const PRIVACY: LegalDoc = {
    id: 'privacy',
    title: 'Privacy Policy',
    kicker: 'How your information is handled',
    lede: 'What Ask-HVAC.com collects, why, who it goes to, and what you can ask us to do about it.',
    clauses: [
        {
            p: 'Ask-HVAC.com is an educational website that helps homeowners understand heating and cooling decisions, and — only if you ask for it — passes your request to an HVAC professional. This policy describes what we collect and what happens to it. It covers this website only.',
        },
        {
            h: 'The short version',
            list: [
                'You can read every page of this site, and use the troubleshooters, calculators and matchers, without giving us your name.',
                'We only collect contact details when you fill in a form and tick the consent box.',
                'When you submit a request for professional help, your details are passed to an HVAC professional so they can contact you. That is the entire point of the form, and it is the main way your information leaves us.',
                'We do not sell your personal information, and we do not use advertising or analytics trackers on this site.',
            ],
        },
        {
            h: 'Information you give us',
            p: 'When you submit the second opinion or contact form, we collect what you put into it:',
            list: [
                'Your name.',
                'Your phone number and/or email address — at least one, so someone can reply.',
                'The service address for the property, where you provide it.',
                'What you tell us about your situation in the description field.',
                'Your answers to the optional intake and qualifier questions.',
                'Your explicit consent to be contacted, which is a tick box that is never pre-ticked.',
            ],
        },
        {
            h: 'Information the site creates as you use it',
            list: [
                'Decision-tree answers. When you walk through a troubleshooter, the repair-or-replace assessment, or one of the matchers, your answers are held in your browser. They are only sent to us if you ask for an AI analysis or attach them to a request for help.',
                'AI analysis sessions. If you request the optional AI read, the answers you gave and the resulting analysis are stored so the same request can be repeated without running it twice.',
                'An anonymous device identifier. On your first AI request we set a signed cookie containing a random identifier, valid for up to 365 days. It carries no personal information.',
                'Your IP address, recorded alongside AI usage.',
                'Basic server logs generated by operating a website.',
            ],
        },
        {
            h: 'Why we use the device identifier and IP address',
            p: 'The AI analysis is free and costs us money per use, so it is capped. The device cookie and IP address are how that cap is counted and how automated abuse is limited. They are not used to build a profile of you, to target advertising, or to track you across other websites. If you block or clear the cookie, the cap simply counts against your IP address instead.',
        },
        {
            h: 'Maintenance reminders',
            p: 'If you sign up for maintenance reminders, we store your email address, the site you signed up through, and the reminder schedule and topics you chose. Verification tokens are stored only as hashes, never as readable codes. You can ask to be unsubscribed at any time.',
        },
        {
            h: 'Who we share information with',
            p: 'We share your information in these situations, and no others:',
            list: [
                'HVAC professionals. When you consent to be contacted, your request — including the details you provided and any decision-tree answers you attached — is passed to an HVAC professional working with a licensed HVAC contractor, so they can contact you about your enquiry. Once your details are with them, their own privacy practices apply to what they do next; we do not control that.',
                'Our AI provider. If you request the optional AI analysis, the relevant answers and description are sent to Anthropic to generate it. Do not type anything into that field you would not want sent to a third-party service.',
                'Address lookup. Where address autocomplete is enabled, what you type into the address box is sent through our server to the Google Places API to suggest matching addresses. The request goes via our server rather than directly from your browser.',
                'Service providers who host and operate this site and its database, under contract.',
                'Where we are required to by law, or where it is necessary to protect the security of the service.',
            ],
        },
        {
            h: 'What we do not do',
            list: [
                'We do not sell your personal information.',
                'We do not run advertising trackers, analytics pixels, or third-party marketing tags on this site.',
                'We do not require an account, a password, or a login to use anything here.',
                'We do not share your contact details with anyone beyond what is described above.',
            ],
        },
        {
            h: 'How long we keep things',
            p: 'Lead and contact records are kept for as long as needed to deliver the request and to keep a record of the consent you gave. AI usage records are kept to operate the usage caps. The device cookie expires after 365 days. You can ask us to delete your information, and we will do so unless we are required to keep it.',
        },
        {
            h: 'Your choices',
            list: [
                'You can use most of this site without submitting anything.',
                'You can ask us what we hold about you, ask for it to be corrected, or ask for it to be deleted.',
                'You can ask to be unsubscribed from reminder emails.',
                'You can clear the device cookie in your browser at any time.',
                'Depending on where you live, you may have additional rights under your local privacy law. Contact us and we will honour any right that applies to you.',
            ],
        },
        {
            h: "Children's privacy",
            p: 'This site is intended for adult homeowners and is not directed at children. We do not knowingly collect personal information from children. If you believe a child has submitted information, contact us and we will delete it.',
        },
        {
            h: 'Security',
            p: 'We take reasonable measures to protect the information we hold, including encrypted connections and hashed verification tokens. No website or database is perfectly secure, and we cannot guarantee absolute security.',
        },
        {
            h: 'Changes to this policy',
            p: 'If this policy changes, the updated version will be posted on this page with a new date. Material changes to how we handle information will be reflected here before they take effect.',
        },
        { h: 'Contact', p: CONTACT_LINE },
    ],
}

/* ------------------------------------------------------------------ */
/* Terms of Use                                                        */
/* ------------------------------------------------------------------ */

const TERMS: LegalDoc = {
    id: 'terms',
    title: 'Terms of Use',
    kicker: 'The agreement for using this site',
    lede: 'What Ask-HVAC.com is, what it is not, and the terms on which you use it.',
    clauses: [
        {
            p: 'By using Ask-HVAC.com you agree to these terms. If you do not agree with them, please do not use the site.',
        },
        {
            h: 'What this site is',
            p: 'Ask-HVAC.com is an educational and decision-support website for homeowners. It explains how heating and cooling systems work, helps you think through repair-versus-replacement decisions, describes equipment and indoor air quality options, gives broad cost context, and can pass a request for help to an HVAC professional if you ask it to.',
        },
        {
            h: 'What this site is not',
            p: 'This is the most important section on this page. Ask-HVAC.com is not an HVAC contractor. We do not perform diagnosis, inspection, repair, installation or maintenance, we hold no contractor licence, and we warrant no equipment or workmanship. Nothing on this site is a professional HVAC diagnosis, an engineering determination, a guaranteed price, or a substitute for evaluation by a qualified HVAC professional. See the full Disclaimer for detail.',
        },
        {
            h: 'Educational use only',
            p: 'Everything on this site — including assessments, guided troubleshooting, repair-or-replace guidance, equipment comparisons, cost ranges and any AI-generated analysis — is provided for general educational purposes. You use it at your own discretion and at your own risk, and you remain responsible for the decisions you make about your property.',
        },
        {
            h: 'Safety',
            p: 'Do not use this site to diagnose or work on anything that could hurt you. If you smell gas or burning, if you see smoke or sparks, or if you suspect a carbon monoxide problem, stop, leave the building, and call your gas utility or emergency services. Do not attempt work involving refrigerant, gas piping, or live electrical circuits. Refrigerant handling in particular is legally restricted to certified technicians.',
        },
        {
            h: 'Connecting with an HVAC professional',
            p: 'If you consent, we pass your request to an HVAC professional working with a licensed HVAC contractor so they can contact you. Please understand what that does and does not mean:',
            list: [
                'We do not operate a vetted contractor network and we do not carry out licensing, insurance, background or quality checks on your behalf.',
                'We do not employ, control, supervise or guarantee any professional or contractor, and none of them acts as our agent.',
                'Any agreement, quote, price, schedule, warranty or work is strictly between you and that contractor. We are not a party to it.',
                'You are responsible for satisfying yourself about any contractor before hiring them — including confirming their licence, insurance and references. Your state or local licensing authority is the place to verify a licence.',
                'We are not responsible for the acts, omissions, workmanship, pricing or conduct of any contractor or professional.',
            ],
        },
        {
            h: 'AI-generated content',
            p: 'Parts of this site can generate an analysis using an AI system. AI output can be wrong, incomplete or misleading, and the system has not seen your equipment. Treat it as a starting point for a conversation with a qualified professional, never as a finding. Do not rely on it for any safety-related decision.',
        },
        {
            h: 'Cost information',
            p: 'Any price or range shown on this site is a broad general estimate for educational purposes. It is not a quote, not a guaranteed or standard industry price, and not a statement of what a contractor should charge. Actual prices vary by contractor, equipment, parts, labor, accessibility, geographic area and the specifics of the job. Only an on-site evaluation produces a real price.',
        },
        {
            h: 'Financing',
            p: 'We do not provide financing, arrange credit, act as a lender or broker, or determine any financing terms. Nothing on this site is an offer of credit. Financing may be available through the contractor you choose, and all programs, rates, terms and qualification requirements are theirs and their lender\'s.',
        },
        {
            h: 'Acceptable use',
            p: 'Please use the site normally and in good faith. You agree not to:',
            list: [
                'Use the site for any unlawful purpose, or to submit anyone else\'s personal information without their permission.',
                'Submit false or misleading information through the forms.',
                'Attempt to circumvent usage limits, interfere with the service, or access it by automated means.',
                'Copy or republish substantial parts of the site\'s content as your own.',
            ],
        },
        {
            h: 'Intellectual property',
            p: 'The content, design and code of this site belong to their owner and are protected by copyright. You may read, print and share pages for your own personal, non-commercial use. Anything else needs permission.',
        },
        {
            h: 'No warranty',
            p: 'The site is provided "as is" and "as available", without warranties of any kind, whether express or implied, including any implied warranty of merchantability, fitness for a particular purpose, accuracy or non-infringement. We do not warrant that the site will be uninterrupted, error-free, or that any information on it is complete or current.',
        },
        {
            h: 'Limitation of liability',
            p: 'To the fullest extent permitted by law, Ask-HVAC.com and its operators are not liable for any indirect, incidental, special, consequential or punitive damages, or for any loss of property, equipment, income or data, arising out of your use of this site, your reliance on anything on it, or any dealing with a contractor or professional you were connected with through it. Some jurisdictions do not allow certain limitations, so parts of this may not apply to you.',
        },
        {
            h: 'Third-party links',
            p: 'Where the site links to other websites, we do not control them and are not responsible for their content or practices.',
        },
        {
            h: 'Changes',
            p: 'We may update these terms. The current version is always the one posted on this page, and continuing to use the site after a change means you accept the updated terms. We may also change or discontinue parts of the site.',
        },
        {
            h: 'Governing law',
            p: 'These terms are governed by the laws of the State of Florida, without regard to its conflict of law rules.',
        },
        { h: 'Contact', p: CONTACT_LINE },
    ],
}

/* ------------------------------------------------------------------ */
/* HVAC / Website Disclaimer                                           */
/* ------------------------------------------------------------------ */

const DISCLAIMER: LegalDoc = {
    id: 'disclaimer',
    title: 'HVAC & Website Disclaimer',
    kicker: 'The limits of everything on this site',
    lede: 'Ask-HVAC.com is educational. It is not a diagnosis, not an inspection, not an engineering determination, and not a price.',
    clauses: [
        {
            h: 'Educational information only',
            p: 'Everything published on Ask-HVAC.com — including assessments, guided troubleshooting, repair and replacement guidance, pricing ranges, equipment and system information, indoor air quality information, and any AI-generated analysis — is provided for general educational and informational purposes only.',
        },
        {
            h: 'This is not a professional evaluation',
            p: 'Specifically, nothing on this site is any of the following:',
            list: [
                'An on-site HVAC diagnosis. We have not seen, measured, tested or inspected your equipment. No amount of accurate answering changes that.',
                'An inspection or a safety certification of any kind.',
                'An engineering determination, a load calculation, or a substitute for one.',
                'A guaranteed price, a quote, a bid, or an estimate of what your job will cost.',
                'A substitute for evaluation by a qualified HVAC professional.',
                'A recommendation of any particular contractor, brand, model or product.',
            ],
        },
        {
            h: 'Why a real evaluation matters',
            p: 'HVAC systems fail in ways that look identical from the outside and are completely different underneath. The same symptom — weak cooling, a system that will not start, a rising bill — can come from a dozen causes ranging from a dirty filter to a failed compressor. Distinguishing between them requires measurements taken at the equipment by someone qualified to take them. This site can help you understand what those possibilities are and what to ask about. It cannot tell you which one you have.',
        },
        {
            h: 'About the assessments and calculators',
            p: 'The repair-or-replace assessment and the matchers apply published rules to the answers you give. They are transparent by design — every factor and its weight is shown — but they work only from what you tell them, they use general rules of thumb rather than facts about your specific equipment, and a wrong or approximate answer produces a wrong result. Treat any output as one input to your decision, not as the decision.',
        },
        {
            h: 'About AI-generated information',
            p: 'Some features generate an analysis using an artificial intelligence system. AI output can be inaccurate, incomplete, outdated or simply wrong, and it may sound confident while being none of those things. It has not inspected your system. Always have anything it suggests confirmed by a qualified HVAC professional before spending money or acting on it, and never rely on it for a decision with a safety dimension.',
        },
        {
            h: 'About pricing information',
            p: 'Any cost figure on this site is a broad, general, educational estimate. It is not a quote, not a guaranteed price, and not a standard or fair industry price. Actual prices vary considerably by contractor, equipment, parts, labor, accessibility, geographic area, local market conditions and the specifics of the work. A contractor quoting outside a range shown here is not necessarily overcharging. Only an on-site evaluation by a qualified HVAC professional can produce a real price for your property.',
        },
        {
            h: 'Safety warning',
            p: 'Heating and cooling equipment involves high-voltage electricity, pressurised refrigerant, combustion, and in many homes natural gas. Serious injury and death are possible. Do not attempt work you are not qualified and equipped to do safely. Refrigerant handling legally requires EPA Section 608 certification; gas and electrical work is regulated in most jurisdictions. If you smell gas or burning, see smoke, or suspect carbon monoxide, leave the building immediately and call your gas utility or emergency services — do not stop to read anything on this site first.',
        },
        {
            h: 'No professional relationship',
            p: 'Using this site does not create a contractor–client, engineering, advisory or professional relationship of any kind between you and Ask-HVAC.com. We are not your contractor and we are not acting on your behalf.',
        },
        {
            h: 'About contractors you are connected with',
            p: 'If you ask us to, we pass your request to an HVAC professional working with a licensed HVAC contractor. We do not vet, license, endorse, employ, supervise or guarantee anyone, and we do not verify credentials on your behalf. Confirm licensing and insurance yourself before hiring anyone, and understand that any work, quote, agreement or warranty is strictly between you and that contractor.',
        },
        {
            h: 'Accuracy and currency',
            p: 'HVAC standards, refrigerants, efficiency ratings, regulations and prices all change. We aim to keep this site accurate but make no warranty that any of it is complete, current or applicable to your situation, and we are not liable for any decision made in reliance on it.',
        },
        {
            h: 'Your responsibility',
            p: 'You are responsible for the decisions you make about your property and equipment. Use this site as one source of understanding among several, and have a qualified professional confirm anything before you act on it.',
        },
        { h: 'Contact', p: CONTACT_LINE },
    ],
}

export const LEGAL_DOCS: Record<LegalDoc['id'], LegalDoc> = {
    privacy: PRIVACY,
    terms: TERMS,
    disclaimer: DISCLAIMER,
}
